import { useMemo, useState } from "react";
import {
  colorDistance,
  createPalette,
  type Hsb,
  MAX_TOTAL,
  randomSeed,
  type RoundColor,
  roundScore,
  scoreLabel,
} from "./color";
import { copy } from "./copy";
import { GameBar } from "./components/GameBar";
import { Hero } from "./components/Hero";
import { IntroStage, Stage } from "./components/Stage";
import { Landing } from "./components/Landing";
import { Results, type RoundResult } from "./components/Results";
import { ReviewStage, type ReviewData } from "./components/ReviewStage";
import { Scoreboard } from "./components/Scoreboard";

type Phase = "intro" | "playing" | "reviewing" | "done";

const defaultGuess: Hsb = { hue: 200, saturation: 55, brightness: 70 };
const bestKey = "toont-one-best";

const readBest = () => {
  try {
    return Number(localStorage.getItem(bestKey) ?? 0) || 0;
  } catch {
    return 0;
  }
};

const writeBest = (value: number) => {
  try {
    localStorage.setItem(bestKey, String(value));
  } catch {
    // ignore storage errors (private mode, quota)
  }
};

const buildResultText = (results: RoundResult[], total: number) =>
  `${copy.brand} ${total}/${MAX_TOTAL}점\n${results
    .map((item, index) => `${index + 1}라 ${item.score}점`)
    .join(" · ")}\nhttps://toont.one`;

export function App() {
  const [seed, setSeed] = useState<number>(() => randomSeed());
  const palette: RoundColor[] = useMemo(() => createPalette(seed), [seed]);
  const [phase, setPhase] = useState<Phase>("intro");
  const [roundIndex, setRoundIndex] = useState(0);
  const [guess, setGuess] = useState<Hsb>(defaultGuess);
  const [results, setResults] = useState<RoundResult[]>([]);
  const [review, setReview] = useState<ReviewData | null>(null);
  const [notice, setNotice] = useState("");
  const [best, setBest] = useState(readBest);

  const target = palette[Math.min(roundIndex, palette.length - 1)];
  const total = results.reduce((sum, item) => sum + item.score, 0);
  const average = results.length ? Math.round(total / results.length) : 0;
  const isLastRound = roundIndex === palette.length - 1;
  const lastResult = results.length ? results[results.length - 1] : null;
  const lastRoundSummary = lastResult
    ? { score: lastResult.score, label: scoreLabel(lastResult.score) }
    : null;

  const startNew = () => {
    setSeed(randomSeed());
    setRoundIndex(0);
    setResults([]);
    setReview(null);
    setNotice("");
    setGuess(defaultGuess);
    setPhase("playing");
  };

  const submitGuess = () => {
    const score = roundScore(target, guess);
    const distance = colorDistance(target, guess);
    const next: RoundResult = { target, guess, score, distance };
    setResults((prev) => [...prev, next]);
    setReview({ target, guess, score, distance });
    setPhase("reviewing");
  };

  const advance = () => {
    if (!review) return;
    if (isLastRound) {
      const finalTotal = results.reduce((sum, item) => sum + item.score, 0);
      if (finalTotal > best) {
        writeBest(finalTotal);
        setBest(finalTotal);
      }
      setPhase("done");
      return;
    }
    setRoundIndex((value) => value + 1);
    setGuess(defaultGuess);
    setReview(null);
    setPhase("playing");
  };

  const resultText = buildResultText(results, total);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(resultText);
      setNotice(copy.copied);
    } catch {
      setNotice(copy.copyFailed);
    }
  };

  const shareResult = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: copy.brand, text: resultText, url: "https://toont.one" });
        return;
      } catch {
        // fall through to clipboard fallback
      }
    }
    await copyToClipboard();
  };

  return (
    <main className="shell">
      {phase === "intro" ? (
        <Hero best={best} onStart={startNew} />
      ) : (
        <GameBar
          phase={phase}
          roundIndex={roundIndex}
          total={total}
          best={best}
          onRestart={startNew}
        />
      )}
      <section className="game-layout" aria-label="카툰 색 맞히기">
        {phase === "intro" ? <IntroStage /> : null}
        {phase === "playing" ? (
          <Stage
            target={target}
            guess={guess}
            onGuess={setGuess}
            onSubmit={submitGuess}
            roundIndex={roundIndex}
            roundCount={palette.length}
          />
        ) : null}
        {phase === "reviewing" && review ? (
          <ReviewStage
            review={review}
            roundIndex={roundIndex}
            roundCount={palette.length}
            onNext={advance}
          />
        ) : null}
        {phase === "done" ? (
          <Results
            results={results}
            total={total}
            best={best}
            onShare={shareResult}
            onCopy={copyToClipboard}
            onRestart={startNew}
          />
        ) : null}
      </section>
      {phase === "playing" && lastRoundSummary ? (
        <p className="round-score last-round">
          {copy.lastRound} {lastRoundSummary.score}점 · {lastRoundSummary.label}
        </p>
      ) : null}
      {notice ? (
        <p className="notice" role="status">
          {notice}
        </p>
      ) : null}
      <Scoreboard
        resultsCount={results.length}
        total={total}
        average={average}
        best={best}
      />
      <Landing />
    </main>
  );
}
